import { navigateTo } from "#app";
import axios from "axios";
import { defineStore } from "pinia";
import { useCheckoutStore } from "~/store/checkout";
import { useSearchStore } from "~/store/search";
import type User from "~~/types/User";

export const useUserStore = defineStore("userStore", {
    persist: {
        storage: piniaPluginPersistedstate.cookies({
            sameSite: "strict",
            maxAge: 60 * 60 * 12, // 12 hours
        }),
    },
    state: () => ({
        executedFetchUser: false,
        lastUserFetched: Date.now(),
        user: {} as User | null,
        isLoggedIn: false,
        userState: "",
        userZip: "",
        userCounty: "",
        locationFetchFromThirdParty: false,

        loginRedirect: "",
    }),
    getters: {},
    actions: {
        needToRefreshUser() {
            const inXMilliseconds = 15 * 60 * 1000; // 15 minutes
            const millisecondsSince = Date.now() - this.lastUserFetched;

            if (millisecondsSince > inXMilliseconds) {
                return true;
            }

            return false;
        },
        async fetchUser(logOutUnAuthUser = true) {
            const refresh = this.needToRefreshUser();

            try {
                if (this.executedFetchUser && this.user && !refresh) {
                    return this.user;
                }

                if (import.meta.client && (!this.executedFetchUser || refresh)) {
                    const config = useRuntimeConfig();
                    const response = await axios.get(config.public.STATAMIC_API + "/user");

                    if (response.status == 200 && response.data && Object.keys(response.data).length > 0) {
                        this.updateUser(response.data);
                        this.lastUserFetched = Date.now();
                        this.executedFetchUser = true;
                    } // Clear out user data just in case
                    else {
                        this.removeUser(logOutUnAuthUser);
                    }
                }
            } catch (e: any) {
                console.log("e:", e);
                // User not logged in will return 401 unauthorized. Redirect if requested.
                if (e.response?.status === 401) {
                    this.removeUser(logOutUnAuthUser);
                }
            }

            return this.user;
        },
        updateUser(user: any) {
            this.user = user;
            this.isLoggedIn = true;
        },
        removeUser(logOutUnAuthUser = true) {
            useCheckoutStore().clearEnteredData();
            this.user = null;
            this.isLoggedIn = false;
            this.executedFetchUser = false;

            if (logOutUnAuthUser) {
                navigateTo(`/home/${this.userState ?? "ga"}`);
            }
        },
        updateUserState(state: string) {
            if (state) {
                this.userState = state.toLowerCase();
                // Update state in search store
                useSearchStore().updateSelectedState(this.userState);
            }
        },
        updateUserZip(zip: string) {
            if (zip) {
                this.userZip = zip;
            }
        },
        async fetchUserState() {
            try {
                // No need to fetch IP info if state is already provided in URL. Example: /products/state/ga/page/1
                const route = useRoute();
                if (
                    route.path.includes("/products/state/") ||
                    route.path.includes("/home/") ||
                    route.path.includes("/catalog/")
                ) {
                    let stateParam = route.params?.state ?? "";
                    if (route.path.includes("/products/state/")) {
                        const slug = route.params?.slug as string[];
                        stateParam = slug[1] ?? "";
                    }

                    if (stateParam) {
                        this.updateUserState(stateParam as string);
                        return this.userState;
                    }
                }

                if (this.userState && this.locationFetchFromThirdParty) {
                    this.updateUserState(this.userState); // Ensure state is lowercase and update search store
                    return this.userState;
                }

                // IP lookup and default fallback are client-only.
                // Server cannot know the user's real IP, and setting a server-side default
                // (e.g. "ga") would differ from the client's IP-based result, causing
                // a Nuxt hydration mismatch on any element driven by selectedState.
                if (import.meta.server) {
                    return this.userState;
                }

                if (!this.userState || !this.userZip || !this.locationFetchFromThirdParty) {
                    console.log("Fetching location info from third party service...");
                    const response = (await fetch("https://ipapi.co/json/").then((r) => r.json())) as {
                        region_code: string;
                        postal: string;
                    };

                    if (response?.region_code) {
                        this.updateUserState(response.region_code);
                        this.updateUserZip(response.postal);
                        this.locationFetchFromThirdParty = true;
                        return this.userState;
                    }
                }

                // Default to GA if we can't get the info (client-only)
                this.updateUserState("ga");
                this.updateUserZip("30033");
                return this.userState;
            } catch (e: any) {
                //console.log("e:", e);
            }

            return this.userState;
        },
        updateLoginRedirect(path: string) {
            if (path != "/login") {
                this.loginRedirect = path;
            }
        },
    },
});
